/**
 * VoiceCircle Component
 * Displays the voice mode interface with circular gradient background,
 * voice waves/dots, and control buttons (pause/resume and stop)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { AudioWaveform } from './AudioWaveform';
import { Cross2Icon } from '@radix-ui/react-icons';
import { MicOff, Mic } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface VoiceCircleProps {
  isListening: boolean;
  isPaused: boolean;
  audioLevel: number;
  frequencyData?: Uint8Array;
  isRestarting?: boolean; // Show spinner when restarting mic after pause
  showInactivityWarning?: boolean; // Show "Paused" text when force paused
  hasFunctionCallInProgress?: boolean; // Show spinner when function is running
  onPauseToggle: () => void;
  onStop: () => void;
  isDarkMode: boolean;
}

export const VoiceCircle: React.FC<VoiceCircleProps> = ({
  isListening,
  isPaused,
  audioLevel,
  frequencyData,
  isRestarting = false,
  showInactivityWarning = false,
  hasFunctionCallInProgress = false,
  onPauseToggle,
  onStop,
  isDarkMode
}) => {
  // Smooth audio level with easing (like the article's approach)
  const [smoothLevel, setSmoothLevel] = useState(0);
  
  useEffect(() => {
    let rafId: number;
    const update = () => {
      // Amplify audio level significantly for more visible effects
      const amplifiedLevel = Math.min(1, audioLevel * 50); // Multiply by 50 instead of letting it be tiny
      setSmoothLevel(prev => prev + (amplifiedLevel - prev) * 0.25);
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafId);
  }, [audioLevel]);

  // Dynamic visual parameters
  const scale = 1 + smoothLevel * 0.05; // Reduced to 0.05 for minimal expansion
  const glowOpacity = 0.1 + smoothLevel * 0.2; // Further reduced to 0.1-0.3 range
  const glowBlur = 60 + smoothLevel * 15; // Further reduced blur to 60-75px

  // Calculate subtle shadow pulse
  const shadowStyle = useMemo(() => {
    const opacity = 0.3 + smoothLevel * 0.25;
    const blur = 24 + smoothLevel * 12;
    return `0 8px ${blur}px rgba(52, 115, 222, ${opacity})`;
  }, [smoothLevel]);

  return (
    <>
      <AnimatePresence mode="wait">
        {/* Full-width background layer - positioned to clear legal disclaimer */}
        <div 
          key="voice-circle-container"
          className="absolute left-0 right-0" 
          style={{ bottom: '32px', zIndex: 40, pointerEvents: 'none' }}
        >
          <div className="w-full py-4 max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-10 xl:px-16 2xl:px-24" style={{ pointerEvents: 'none' }}>
            <motion.div
              key="voice-circle-motion"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                pointerEvents: 'none',
              }}
            >
        {/* Voice Circle with shader-based iridescent effect */}
        <div style={{ position: 'relative', width: '160px', height: '160px' }}>
          {/* Outer glow layer - blurred background */}
          <div
            style={{
              position: 'absolute',
              inset: '-50px',
              borderRadius: '50%',
              background: `radial-gradient(circle, rgba(59, 130, 246, ${glowOpacity}) 0%, transparent 70%)`,
              filter: `blur(${glowBlur}px)`,
              pointerEvents: 'none',
            }}
          />
          
          {/* Main orb with iridescent shader */}
          <div
            style={{
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: shadowStyle,
              position: 'relative',
              pointerEvents: 'none',
              transform: `scale(${scale})`,
              transition: 'transform 0.12s ease-out, box-shadow 0.15s ease-out',
            }}
          >
            {/* Base gradient with multiple blue shades */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at 50% 50%, 
                  #7aa5f7 0%, 
                  #5a8df7 20%, 
                  #3473de 40%, 
                  #2563cc 60%, 
                  #1d4fa8 80%, 
                  #163d85 100%)`,
              }}
            />
            
            {/* Audio-reactive lighter overlay - gets stronger when speaking */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at 50% 50%, 
                  rgba(154, 184, 255, ${Math.min(0.9, smoothLevel * 2)}) 0%, 
                  rgba(90, 141, 247, ${Math.min(0.7, smoothLevel * 1.5)}) 30%, 
                  rgba(52, 115, 222, ${Math.min(0.5, smoothLevel)}) 60%, 
                  transparent 100%)`,
                transition: 'background 0.05s ease-out',
              }}
            />
            
            {/* Pulsing wave effect */}
            <motion.div
              style={{
                position: 'absolute',
                inset: '-10%',
                background: `radial-gradient(circle at 50% 50%, 
                  rgba(154, 184, 255, ${Math.min(0.8, smoothLevel * 1.5)}) 0%, 
                  transparent 60%)`,
                filter: `blur(30px)`,
                opacity: Math.max(0.3, smoothLevel),
              }}
              animate={{
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          
            {/* Inner content container - waves on top of gradient */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
              }}
            >
          {/* Show spinner when starting up, restarting microphone, or function call in progress */}
          {(isRestarting || !isListening || hasFunctionCallInProgress) ? (
            <div
              className="animate-spin rounded-full border-4 border-white border-t-transparent"
              style={{ width: 48, height: 48 }}
            />
          ) : (
            /* Show voice waves when microphone is active and no function call */
            <>
              {/* Voice waves or dots - stays centered */}
              <AudioWaveform
                isActive={isListening && !isPaused}
                isPaused={isPaused}
                audioLevel={audioLevel}
                frequencyData={frequencyData}
                size={80}
                color="white"
              />
            </>
          )}
          </div>
          </div>
        </div>

        {/* Control buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            pointerEvents: 'auto', // Re-enable pointer events for buttons
          }}
        >
          {/* Pause/Resume button */}
          <button
            type="button"
            onClick={onPauseToggle}
            disabled={isRestarting}
            aria-label={isPaused ? 'Resume voice chat' : 'Pause voice chat'}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: 'none',
              background: isDarkMode ? '#374151' : '#E5E7EB',
              color: isDarkMode ? '#F9FAFB' : '#1F2937',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isRestarting ? 'not-allowed' : 'pointer',
              opacity: isRestarting ? 0.5 : 1,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            onMouseEnter={(e) => {
              if (!isRestarting) {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
          >
            {isPaused ? (
              <MicOff size={24} strokeWidth={2} />
            ) : (
              <Mic size={24} strokeWidth={2} />
            )}
          </button>

          {/* Stop button */}
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop voice mode"
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              border: 'none',
              background: '#EF4444',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
              e.currentTarget.style.background = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.background = '#EF4444';
            }}
          >
            <Cross2Icon width={24} height={24} />
          </button>
        </div>

            </motion.div>
          </div>
        </div>
      </AnimatePresence>
    </>
  );
};
